import { Manipulation } from 'swiper/modules';
import { breakpoints } from '../../../../src/js/breakpoints.ts';
import { CustomSwiper } from '../../../../src/js/components/swiper/custom-swiper.js';

export class ClientsSwiper {
  private headingsSwiper: CustomSwiper;
  private clientsSwiper: CustomSwiper;
  private logosPerSlide = 0;
  private slidesPerCategory = 0;

  /**
   *
   */
  public constructor() {
    const elHeadingsSwiper = document.querySelector('.swiper--clients-heading');
    const elClientsSwiper = document.querySelector('.swiper--clients');
    if (!(elHeadingsSwiper instanceof HTMLElement) || !(elClientsSwiper instanceof HTMLElement)) {
      throw new Error('Invalid elements provided for Heading Swiper and/or Client Swiper');
    }

    // create the slider instances
    this.headingsSwiper = new CustomSwiper(elHeadingsSwiper, {
      allowTouchMove: false,
      //manipulation: true,
      modules: [Manipulation],
    });
    this.clientsSwiper = new CustomSwiper(elClientsSwiper, {
      grabCursor: true,
      //manipulation: true,
      modules: [Manipulation],
    });

    this.populate(elClientsSwiper);

    // synchronize headings slider position with the clients slider
    this.clientsSwiper.on('slidePrevTransitionStart', (swiper) => {
      if (this.slidesPerCategory === 1 || (swiper.snapIndex - 1) % 2) {
        this.headingsSwiper.slidePrev();
      }
    });
    this.clientsSwiper.on('slideNextTransitionStart', (swiper) => {
      if (this.slidesPerCategory === 1 || !((swiper.snapIndex - 1) % 2)) {
        this.headingsSwiper.slideNext();
      }
    });
  }

  /**
   * @param {HTMLElement} elClientsSwiper
   * @returns {void}
   */
  private populate(elClientsSwiper: HTMLElement): void {
    let clientData = {};
    try {
      clientData = JSON.parse(elClientsSwiper.querySelector('.client-data')?.innerHTML ?? '{}');
    } catch {
      return;
    }

    // populate the slides
    this.headingsSwiper.appendSlide(this.getHeadingSlides(Object.keys(clientData)));

    const mql = matchMedia(`(min-width: ${breakpoints.get('md')}rem)`);
    const populateClientSlides = () => {
      if (mql.matches) {
        this.logosPerSlide = 12;
        this.slidesPerCategory = 1;
      } else {
        this.logosPerSlide = 6;
        this.slidesPerCategory = 2;
      }

      // clear the swiper and rebuild all slides
      this.clientsSwiper.removeAllSlides();
      this.clientsSwiper.appendSlide(this.getClientSlides(clientData, this.logosPerSlide, this.slidesPerCategory));

      this.headingsSwiper.slideToLoop(0);
      this.clientsSwiper.slideToLoop(0);
      //this.clientsSwiper.lazy.load();
    };
    populateClientSlides();

    // on window resize, rebuild the client slides
    mql.addEventListener('change', populateClientSlides);
  }

  /**
   * Create the HTMLElements of each heading slide
   */
  private getHeadingSlides(headings: string[] = []): HTMLElement[] {
    const slides: HTMLElement[] = [];
    for (const heading of headings) {
      const elSlide = document.createElement('div');
      elSlide.classList.add('swiper-slide');
      const elHeading = document.createElement('h3');
      elHeading.innerHTML = heading;
      elSlide.appendChild(elHeading);

      slides.push(elSlide);
    }

    return slides;
  }

  /**
   * Create the HTMLElements of each slide of client logos
   * @param {*} clientData
   * @param {number} logosPerSlide
   * @param {number} slidesPerCategory
   * @returns {HTMLDivElement[]}
   */
  private getClientSlides(clientData: { [key: string]: any; }, logosPerSlide = 0, slidesPerCategory = 0): HTMLElement[] {
    // generate slide(s) for each category
    const slides: HTMLElement[] = [];
    for (const [heading, clients] of Object.entries(clientData)) {
      const elLogos = this.getClientLogoElements(clients);

      for (let i = 0; i < slidesPerCategory; i++) {
        // create the slide and the preloader placeholder
        const elSlide = document.createElement('div');
        elSlide.dataset['category'] = heading;
        elSlide.classList.add('swiper-slide');
        const elPreloaderLayer = document.createElement('div');
        elPreloaderLayer.classList.add('swiper--clients__preloader-layer');
        const elPreloader = document.createElement('div');
        elPreloader.classList.add('swiper-lazy-preloader');
        elPreloaderLayer.appendChild(elPreloader);
        elSlide.appendChild(elPreloaderLayer);

        // identify the client logos for this slide
        const startIndex = logosPerSlide * i;
        const logos = elLogos.slice(startIndex, startIndex + logosPerSlide);

        // populate the slide with the client logos
        const elLogosWrapper = document.createElement('ul');
        elLogosWrapper.classList.add('swiper--clients__logos');
        elLogosWrapper.append(...logos);
        elSlide.appendChild(elLogosWrapper);

        slides.push(elSlide);
      }
    }

    return slides;
  }

  /**
   * Create the HTMLElements for each of the client logos
   * @param {object[]} clients
   */
  private getClientLogoElements(clients: any[]): HTMLElement[] {
    return clients.map(client => {
      const elLogo = document.createElement('li');
      elLogo.classList.add('swiper--clients__logo');

      // create a target element and populate with link for client website (if provided)
      let elTarget;
      if (client.website) {
        elTarget = document.createElement('a');
        elTarget.setAttribute('href', client.website);
        elTarget.setAttribute('target', '_blank');
      } else {
        elTarget = document.createElement('div');
      }
      elTarget.classList.add('swiper--clients__logo__target');

      // const elPlaceholder = document.createElement("img");
      // elPlaceholder.classList.add("swiper-lazy-preloader");
      // elTarget.appendChild(elPlaceholder);

      const elImage = document.createElement('img');
      elImage.setAttribute('loading', 'lazy');
      //elImage.setAttribute("src", "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=");
      //elImage.dataset['src'] = client.logo;
      elImage.setAttribute('src', client.logo);
      elImage.setAttribute('alt', client.name);
      elTarget.appendChild(elImage);

      elLogo.appendChild(elTarget);

      return elLogo;
    });
  }
}

(() => {
  const onReady = () => {
    // create 'Clients' swiper
    try {
      new ClientsSwiper();
    } catch {
      // fail silently
    }
  };

  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', onReady) : onReady();
})();
